// apps/web/src/lib/estoque/localizacoes.ts — Fase 13, Plano 09 (EST-07, EST-08, D-21).
// Regras puras da tela Localizações: ordem da lista, ações permitidas por permissão e mensagens de erro do contrato.
import type { LocalizacaoView } from '@pieltec/contracts';

/** As 5 localizações fixas, na ordem em que aparecem no topo da lista. */
export const NOMES_LOCALIZACOES_FIXAS = ['Recebimento', 'Almoxarifado', 'Produção', 'Qualidade', 'Expedição'] as const;

export const PERMISSAO_CONFIGURAR_ESTOQUE = 'estoque:configurar';

export const APOIO_LOCALIZACAO_FIXA = 'Usada pelos movimentos automáticos. Não pode ser alterada nem excluída.';
export const APOIO_SO_FIXAS = 'Cadastre localizações adicionais, como prateleiras ou câmara fria, quando precisar.';
export const MENSAGEM_LOCALIZACAO_DUPLICADA = 'Já existe uma localização com este nome.';
export const MENSAGEM_LOCALIZACAO_COM_SALDO = 'Esta localização tem saldo. Transfira o material antes de inativar.';
export const MENSAGEM_LOCALIZACAO_VERSAO = 'Esta localização foi alterada por outro usuário. Recarregue para ver as mudanças antes de salvar.';

export type AcaoLocalizacao = 'editar' | 'inativar';

function posicaoFixa(nome: string): number {
  const indice = NOMES_LOCALIZACOES_FIXAS.findIndex((fixo) => fixo.localeCompare(nome, 'pt-BR', { sensitivity: 'base' }) === 0);
  return indice === -1 ? NOMES_LOCALIZACOES_FIXAS.length : indice;
}

/** Fixas no topo (ordem de negócio); depois as cadastradas por nome (pt-BR). Não altera a lista de entrada. */
export function ordenarLocalizacoes(lista: readonly LocalizacaoView[]): LocalizacaoView[] {
  return [...lista].sort((a, b) => {
    if (a.fixa !== b.fixa) return a.fixa ? -1 : 1;
    if (a.fixa) return posicaoFixa(a.nome) - posicaoFixa(b.nome);
    return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
  });
}

/** Fixa: nenhuma ação. Cadastrada: renomear/inativar só com estoque:configurar (e inativar só se ainda ativa). */
export function acoesDaLocalizacao(localizacao: Pick<LocalizacaoView, 'fixa' | 'ativa'>, permissoes: readonly string[]): AcaoLocalizacao[] {
  if (localizacao.fixa || !permissoes.includes(PERMISSAO_CONFIGURAR_ESTOQUE)) return [];
  return localizacao.ativa ? ['editar', 'inativar'] : ['editar'];
}

/** Só as fixas na lista: a tela convida a cadastrar mais. */
export function somenteFixas(lista: readonly LocalizacaoView[]): boolean {
  return lista.length > 0 && lista.every((local) => local.fixa);
}

export function contagemLocalizacoes(total: number): string {
  return total === 1 ? '1 localização' : `${total} localizações`;
}

/** Código de negócio da API -> mensagem em pt-BR do UI-SPEC. null quando o código não é de localização. */
export function mensagemErroLocalizacao(codigo: string | null | undefined): string | null {
  switch (codigo) {
    case 'LOCALIZACAO_DUPLICADA':
      return MENSAGEM_LOCALIZACAO_DUPLICADA;
    case 'LOCALIZACAO_COM_SALDO':
      return MENSAGEM_LOCALIZACAO_COM_SALDO;
    case 'LOCALIZACAO_FIXA':
      return APOIO_LOCALIZACAO_FIXA;
    case 'LOCALIZACAO_VERSAO_DESATUALIZADA':
      return MENSAGEM_LOCALIZACAO_VERSAO;
    default:
      return null;
  }
}
